import React, { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import Copy from 'Src/components/Copy';
import FontIcon from 'Src/components/icons/FontIcon';
import { Flex } from 'Src/components/layout/Flex';
import { useScrollShadow } from 'Src/components/useScrollShadow';
import { useCheckItemName } from 'Src/feature/check-item/useCheckItemName';
import { CheckDetailPrintCheckBox } from 'Src/feature/check-print/CheckDetailPrintCheckBox';
import { PrintJobStatusCell } from 'Src/feature/check-print/PrintJobStatusCell';
import { useCheckPrints } from 'Src/feature/check-print/useCheckPrints';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { ModalActions } from 'Src/feature/modal/ModalActions';
import { ModalContent } from 'Src/feature/modal/ModalContent';
import useRoute from 'Src/routes/useRoute';

import styles from './CheckDetailPrintJobsTab.module.scss';

const sectionId = 'CheckDetailPrintJobsTab';

interface CheckDetailPrintJobsTabProps {
  disableDoneAction: boolean;
  onClose: () => void;
}

const CheckDetailPrintJobsTab: FC<CheckDetailPrintJobsTabProps> = ({ disableDoneAction, onClose }) => {
  const { t } = useTranslation();
  const { route } = useRoute();
  const checkPrints = useCheckPrints({ checkId: parseInt(route.params.active.checkId || '-1') });
  const { getCheckItemName } = useCheckItemName();
  const scrollContainerRef = useRef(null);
  useScrollShadow({
    targetRef: scrollContainerRef,
    mode: 'vertical',
    shadowColor: 'rgba(0, 0, 0, 0.3)',
    applyToTarget: true,
  });

  return (
    <>
      <ModalContent data-testid={`${sectionId}-modal-content`} className={styles.root}>
        <div className={classNames(styles.grid, styles.gridHeader)}>
          <div className={styles.col1Header}>
            <CheckDetailPrintCheckBox
              checked={true}
              onClick={() => {
                // TODO: add multi-select when api supports it
              }}
            />
          </div>
          <div className={styles.col2Header}>
            <Copy variant="body" size="sm" weight="semi-bold" value={t('labels.time')}/>
          </div>
          <div className={styles.col3Header}>
            <Copy variant="body" size="sm" weight="semi-bold" value={t('labels.item')}/>
          </div>
          <div className={styles.col4Header}>
            <Copy variant="body" size="sm" weight="semi-bold" value={t('labels.printer')}/>
          </div>
          <div className={styles.col5Header}>
            <Copy variant="body" size="sm" weight="semi-bold" value={t('labels.status')}/>
          </div>
        </div>

        <div ref={scrollContainerRef} className={classNames(styles.grid, styles.scrollGrid)}>
          {checkPrints.all.list.map((checkItemPrint, index) => {
            const canSelectCheckItem = checkItemPrint.states.combined.state === 'Failed';
            const isNoPrint = checkItemPrint.print.state === 'NO-PRINT';
            const checkItemName = getCheckItemName({
              checkItem: checkItemPrint.model,
              menuItemName: checkItemPrint.model.menuItem.combinedName,
            });

            let printerName = checkItemPrint.print.printedToBackup
              ? checkItemPrint.print.backup?.name || t('printer.labels.missingPrinterName')
              : checkItemPrint.print.name || t('printer.labels.missingPrinterName');
            if (isNoPrint) {
              printerName = t('printer.labels.noPrinter');
            }

            return (
              <React.Fragment key={checkItemPrint.print.id + index}>
                {/* column 1 */}
                <div className={classNames(styles.col1, { [styles.isNoPrint]: isNoPrint })}>
                  {
                    canSelectCheckItem && (
                      <CheckDetailPrintCheckBox
                        checked={false}
                        onClick={() => {
                          // TODO: add multi-select when api supports it
                        }}
                      />
                    )
                  }
                </div>
                <div className={classNames(styles.col2, { [styles.isNoPrint]: isNoPrint })}>
                  <Copy variant="body" size="sm" weight="semi-bold" value={checkItemPrint.print.time}/>
                </div>
                <div className={classNames(styles.col3, { [styles.isNoPrint]: isNoPrint })}>
                  <Copy type="xCount" variant="body" size="sm" value={checkItemPrint.model.quantity}/>
                  <Flex gap="xs">
                    <Copy variant="body" size="sm" value={checkItemName} />
                    {
                      checkItemPrint.model.modifiers && checkItemPrint.model.modifiers.length && (
                        <Copy
                          variant="caption"
                          color="textSecondary"
                          value={checkItemPrint.model.modifiers.map((modifier) => modifier.name).join(', ')}
                        />
                      )
                    }
                  </Flex>
                </div>
                <div className={classNames(styles.col4, { [styles.isNoPrint]: isNoPrint })}>
                  <Copy
                    variant="body"
                    size="sm"
                    value={printerName}
                  />
                </div>
                <div className={classNames(styles.col5, { [styles.isNoPrint]: isNoPrint })}>
                  <PrintJobStatusCell
                    state={checkItemPrint.print.state}
                    onRetry={() => checkPrints.onRetryAllForCheckItem(checkItemPrint)}
                    showRetryButton
                  />
                </div>
                {
                  // print data from check item modifiers
                  checkItemPrint.modifiers.map((job) => {
                    const canSelectModifier = job.print.state === 'Failed';
                    const isSameJobAsCheckItem = checkItemPrint.print.id === job.print.id;
                    const isNoPrint = job.print.state === 'NO-PRINT';

                    let printerName = job.print.printedToBackup
                      ? job.print.backup?.name || t('printer.labels.missingPrinterName')
                      : job.print.name || t('printer.labels.missingPrinterName');
                    if (isNoPrint) {
                      printerName = t('printer.labels.noPrinter');
                    }
                    return (
                      <React.Fragment key={job.id}>
                        <div className={classNames(styles.col1, { [styles.isNoPrint]: isNoPrint })}>
                          {
                            (!isSameJobAsCheckItem && canSelectModifier) && (
                              // omit check box when job is same as the main check
                              <CheckDetailPrintCheckBox
                                // checked={isAllSelected}
                                onClick={() => {
                                  // TODO: add multi-select when api supports it
                                }}
                              />
                            )
                          }
                        </div>
                        <div className={classNames(styles.col2, { [styles.isNoPrint]: isNoPrint })}>
                          {!isSameJobAsCheckItem && (
                            <Copy variant="body" size="sm" weight="semi-bold" value={job.print.time}/>
                          )}
                        </div>
                        <div className={classNames(styles.col3, { [styles.isNoPrint]: isNoPrint })}>
                          <FontIcon name="subdirectory_arrow_right" size="sm"/>
                          <Copy variant="body" size="sm" value={job.model.name}/>
                        </div>
                        <div className={classNames(styles.col4, { [styles.isNoPrint]: isNoPrint })}>
                          <Copy
                            variant="body"
                            size="sm"
                            value={printerName}
                          />
                        </div>
                        <div className={classNames(styles.col5, { [styles.isNoPrint]: isNoPrint })}>
                          <PrintJobStatusCell
                            state={job.print.state}
                            onRetry={() => checkPrints.onRetryJob(job)}
                            showRetryButton={!isSameJobAsCheckItem && canSelectModifier}
                          />
                        </div>
                      </React.Fragment>
                    );
                  })}
              </React.Fragment>
            );
          })}
        </div>
      </ModalContent>
      <ModalActions data-testid={`${sectionId}-modal-actions`}>
        <ModalAction
          color="secondary"
          label={t('labels.done')}
          onClick={onClose}
          disabled={disableDoneAction}
        />
        <ModalAction
          color="primary"
          label={t('labels.retryAll')}
          onClick={checkPrints.onRetryAllForCheck}
        />
      </ModalActions>
    </>
  );
};

export { CheckDetailPrintJobsTab, sectionId };
